import { useRef, useState } from 'react';
import { motion } from 'motion/react';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearchParams } from 'react-router-dom';
import { Send, CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { SelectNativo } from '@/components/ui/select-nativo';
import {
  ErrorAlEnviarLaSolicitud,
  cronometroDelFormulario,
  enviarSolicitudDeMiniPrixia,
} from '@/features/solicitudes/services/solicitudes.service';
import {
  RUBROS_DE_LA_SOLICITUD,
  TAMANOS_DE_CATALOGO,
  type AsuntoDeContacto,
  type Rubro,
  type TamanoDeCatalogo,
} from '@/features/solicitudes/types/solicitudes.types';
import { subjectOptions } from '../data/contact-data';

/*
  Vault 346 (D8): /contact ya no simula el envío. Todo llega a Plataforma › Solicitudes: con «Pedir mi
  Mini Prixia» es una solicitud; con otro asunto, el mensaje se guarda igual con su asunto (ningún
  mensaje se pierde). «Listo» aparece SÓLO después de un 2xx real.
*/

const contactSchema = z.object({
  fullName: z.string().trim().min(2, 'Escribe tu nombre.').max(120),
  email: z.string().trim().email('Escribe un correo válido.').max(254),
  company: z.string().trim().min(2, 'Escribe el nombre de tu empresa.').max(160),
  cargo: z.string().trim().max(120).optional(),
  phone: z.string().trim().max(40).optional(),
  subject: z.string().min(1, 'Elige un asunto.'),
  rubro: z.string().optional(),
  tamano: z.string().optional(),
  message: z.string().trim().max(1000, 'Hasta 1.000 caracteres.').optional(),
  consentimiento: z.literal(true, {
    errorMap: () => ({ message: 'Necesitamos tu permiso para escribirte.' }),
  }),
  /** Trampa: escondida para las personas. */
  sitioWeb: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

/** De los nombres del formulario a los del servidor (para mostrar sus mensajes por campo). */
const CAMPO_DEL_FORMULARIO: Record<string, keyof ContactFormData> = {
  nombre: 'fullName',
  correo: 'email',
  empresa: 'company',
  cargo: 'cargo',
  telefono: 'phone',
  asunto: 'subject',
  rubro: 'rubro',
  tamanoDeCatalogo: 'tamano',
  queQuiereResolver: 'message',
  aceptoContacto: 'consentimiento',
};

/** Los enlaces viejos traían `?asunto=partnership`. */
const ASUNTO_VIEJO: Record<string, string> = { partnership: 'alianza' };

export function ContactForm() {
  const [enviado, setEnviado] = useState<{ nombre: string; correo: string } | null>(null);
  const [fallo, setFallo] = useState<'LIMITE' | 'RED' | null>(null);
  const [searchParams] = useSearchParams();
  const medir = useRef(cronometroDelFormulario()).current;

  // `?asunto=<valor>` preselecciona el asunto (los botones «Pide tu Mini Prixia» de las páginas públicas).
  const asuntoUrl = searchParams.get('asunto') ?? '';
  const asuntoNormalizado = ASUNTO_VIEJO[asuntoUrl] ?? asuntoUrl;
  const asuntoInicial = subjectOptions.some((o) => o.value === asuntoNormalizado)
    ? asuntoNormalizado
    : 'demo-producto';

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      fullName: '',
      email: '',
      company: '',
      cargo: '',
      phone: '',
      subject: asuntoInicial,
      rubro: '',
      tamano: '',
      message: '',
      sitioWeb: '',
    },
  });

  const subjectValue = watch('subject');
  const esMiniPrixia = subjectValue === 'demo-producto';

  const onSubmit = async (d: ContactFormData) => {
    setFallo(null);
    try {
      await enviarSolicitudDeMiniPrixia({
        nombre: d.fullName,
        correo: d.email,
        empresa: d.company,
        cargo: d.cargo || undefined,
        telefono: d.phone || undefined,
        asunto: d.subject as AsuntoDeContacto,
        rubro: esMiniPrixia ? ((d.rubro || '') as Rubro | '') : '',
        tamanoDeCatalogo: esMiniPrixia ? ((d.tamano || '') as TamanoDeCatalogo | '') : '',
        queQuiereResolver: d.message || undefined,
        aceptoContacto: true,
        origen: 'contacto',
        sitioWeb: d.sitioWeb ?? '',
        msEnElFormulario: medir(),
      });
      setEnviado({ nombre: d.fullName, correo: d.email });
    } catch (e) {
      if (e instanceof ErrorAlEnviarLaSolicitud && e.fallo.tipo === 'DATOS') {
        for (const [campo, mensaje] of Object.entries(e.fallo.campos)) {
          const destino = CAMPO_DEL_FORMULARIO[campo];
          if (destino) setError(destino, { message: mensaje });
        }
        return;
      }
      setFallo(
        e instanceof ErrorAlEnviarLaSolicitud && e.fallo.tipo === 'LIMITE' ? 'LIMITE' : 'RED'
      );
    }
  };

  if (enviado) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="flex flex-col items-center justify-center text-center py-16 px-6"
        role="status"
      >
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mb-6">
          <CheckCircle2 className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Listo, {enviado.nombre}.
        </h3>
        <p className="text-gray-500 dark:text-gray-400 max-w-sm">
          {esMiniPrixia
            ? `Te escribimos a ${enviado.correo} para coordinar tu Mini Prixia.`
            : `Te respondemos a ${enviado.correo}.`}
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Escríbenos</h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Pide tu Mini Prixia o déjanos tu consulta. Todo llega al equipo de Prixia.
      </p>

      {/* Trampa para robots: invisible y fuera del orden de tabulación. Una persona nunca la llena. */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-px w-px overflow-hidden">
        <label htmlFor="sitioWeb">Sitio web</label>
        <input
          id="sitioWeb"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          {...register('sitioWeb')}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="fullName" className="text-sm font-medium">
          Nombre <span className="text-destructive">*</span>
        </Label>
        <Input
          id="fullName"
          placeholder="Ana Pérez"
          {...register('fullName')}
          error={errors.fullName?.message}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-sm font-medium">
            Correo de trabajo <span className="text-destructive">*</span>
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="ana@tuempresa.com"
            {...register('email')}
            error={errors.email?.message}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="company" className="text-sm font-medium">
            Empresa <span className="text-destructive">*</span>
          </Label>
          <Input
            id="company"
            placeholder="Tu empresa"
            {...register('company')}
            error={errors.company?.message}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="cargo" className="text-sm font-medium">
            Cargo <span className="text-muted-foreground font-normal text-xs">(opcional)</span>
          </Label>
          <Input
            id="cargo"
            placeholder="Gerente comercial"
            {...register('cargo')}
            error={errors.cargo?.message}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="phone" className="text-sm font-medium">
            Teléfono o WhatsApp{' '}
            <span className="text-muted-foreground font-normal text-xs">(opcional)</span>
          </Label>
          <Input
            id="phone"
            placeholder="+591 7 000 0000"
            {...register('phone')}
            error={errors.phone?.message}
          />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-sm font-medium">
          Asunto <span className="text-destructive">*</span>
        </Label>
        <SearchableSelect
          options={subjectOptions}
          value={subjectValue}
          onValueChange={(val) => setValue('subject', val, { shouldValidate: true })}
          placeholder="Elige un asunto"
          searchPlaceholder="Buscar asunto..."
        />
        {errors.subject && <p className="text-xs text-destructive">{errors.subject.message}</p>}
      </div>

      {esMiniPrixia && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="rubro" className="text-sm font-medium">
              Rubro <span className="text-muted-foreground font-normal text-xs">(opcional)</span>
            </Label>
            <SelectNativo id="rubro" className="w-full" {...register('rubro')}>
              <option value="">Elige tu rubro</option>
              {RUBROS_DE_LA_SOLICITUD.map((r) => (
                <option key={r.valor} value={r.valor}>
                  {r.rotulo}
                </option>
              ))}
            </SelectNativo>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tamano" className="text-sm font-medium">
              Tu catálogo{' '}
              <span className="text-muted-foreground font-normal text-xs">(opcional)</span>
            </Label>
            <SelectNativo id="tamano" className="w-full" {...register('tamano')}>
              <option value="">¿Cuántos productos?</option>
              {TAMANOS_DE_CATALOGO.map((t) => (
                <option key={t.valor} value={t.valor}>
                  {t.rotulo}
                </option>
              ))}
            </SelectNativo>
          </div>
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="message" className="text-sm font-medium">
          {esMiniPrixia ? '¿Qué quieres resolver?' : 'Mensaje'}{' '}
          <span className="text-muted-foreground font-normal text-xs">(opcional)</span>
        </Label>
        <Textarea
          id="message"
          placeholder={
            esMiniPrixia
              ? 'Por ejemplo: saber a cuánto vende mi competencia.'
              : 'Cuéntanos en qué te ayudamos.'
          }
          rows={4}
          maxLength={1000}
          {...register('message')}
          className={errors.message ? 'border-destructive' : ''}
        />
        {errors.message && <p className="text-xs text-destructive">{errors.message.message}</p>}
      </div>

      <div className="space-y-1">
        <label className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-300">
          <input type="checkbox" className="mt-1" {...register('consentimiento')} />
          <span>
            Acepto que Prixia guarde estos datos y me contacte sobre Prixia.{' '}
            <span className="text-destructive">*</span>
          </span>
        </label>
        {errors.consentimiento && (
          <p className="text-xs text-destructive">{errors.consentimiento.message}</p>
        )}
      </div>

      {fallo && (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-destructive/40 p-3 text-sm text-destructive"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {fallo === 'LIMITE'
            ? 'Recibimos varios pedidos desde tu red. Prueba en una hora.'
            : 'No pudimos enviarlo. Revisa tu conexión: lo que escribiste sigue aquí, vuelve a intentarlo.'}
        </div>
      )}

      <Button
        type="submit"
        size="lg"
        disabled={isSubmitting}
        className="w-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-500 hover:to-violet-500 text-white shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Enviando...
          </>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            {fallo === 'RED'
              ? 'Reintentar'
              : esMiniPrixia
                ? 'Pedir mi Mini Prixia'
                : 'Enviar mensaje'}
          </>
        )}
      </Button>
    </form>
  );
}
